import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DataTable,
  TableContainer,
  Table,
  TableBody,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Dropdown,
  MultiSelect,
  DataTableSkeleton,
  ToggleSmall
} from 'carbon-components-react';
import { fetchCLOListing } from 'redux/clo';
import {
  selectCLOListing,
  selectLoadingCLOListing,
  selectAddingCLOAsFavourite,
  selectRemovingCLOAsFavourite,
  selectHidingCLO,
  selectUnhidingCLO
} from 'redux/clo/selectors';
import { selectIsAuthenticated } from 'redux/session/selectors';
import Utils from 'utilities/helper.util';
import CLOModalListingRow from './CLOModalListingRow';
import { CustomPagination } from 'components';
import { SCREEN_SIZE } from 'types';

type Props = {
  enquiryDetail: any;
  handleCLOViewDetailsClick: Function;
  searchText: string;
  setSearchText: Function;
  addressState: string[];
  setAddressState: Function;
  order: string;
  setOrder: Function;
  screenSize: any;
  withHidden: boolean;
  setWithHidden: Function;
  currentPage: number;
  setCurrentPage: Function;
  pageOffset: number;
  setPageOffset: Function;
};

const CLOModalListing: React.FC<Props> = (props: Props) => {
  const {
    enquiryDetail,
    handleCLOViewDetailsClick,
    searchText,
    setSearchText,
    addressState,
    setAddressState,
    order,
    setOrder,
    screenSize,
    withHidden,
    setWithHidden,
    currentPage,
    setCurrentPage,
    pageOffset,
    setPageOffset
  } = props;

  const addressStateListItems = [
    {
      label: 'ACT/NSW',
      value: 'ACT'
    },
    {
      label: 'NT',
      value: 'NT'
    },
    {
      label: 'QLD',
      value: 'QLD'
    },
    {
      label: 'SA',
      value: 'SA'
    },
    {
      label: 'TAS',
      value: 'TAS'
    },
    {
      label: 'VIC',
      value: 'VIC'
    },
    {
      label: 'WA',
      value: 'WA'
    }
  ];

  const getAddressStateOptionItem = (value: string[]) => {
    return addressStateListItems.filter((o) => value.includes(o.value));
  };

  const createAddressStateSearchText = (addressState: string[]) => {
    if (addressState.length === 0) {
      return '';
    }

    const hadACT = addressState.includes('ACT');
    const finalAddressState = hadACT ? [...addressState, 'NSW'] : addressState;
    return finalAddressState.join('|');
  };

  const smallScreen = [SCREEN_SIZE.SM];
  const middleScreen = [SCREEN_SIZE.MD];
  const defaultAddressStateSearchText = createAddressStateSearchText(
    addressState
  );
  const bigScreen = [SCREEN_SIZE.LG, SCREEN_SIZE.XL, SCREEN_SIZE.MAX];
  const dispatch = useDispatch();
  const cloList = useSelector(selectCLOListing);
  const loadingCLOListing = useSelector(selectLoadingCLOListing);
  const addingCLOAsFavourite = useSelector(selectAddingCLOAsFavourite);
  const removingCLOAsFavourite = useSelector(selectRemovingCLOAsFavourite);
  const hidingCLO = useSelector(selectHidingCLO);
  const unhidingCLO = useSelector(selectUnhidingCLO);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [pageSize, setPageSize] = useState<number>(10);
  const [filterBy, setFilterBy] = useState<any>(
    searchText
      ? `name:"%${searchText}%",${
          defaultAddressStateSearchText
            ? `addressState:${defaultAddressStateSearchText},`
            : ''
        }withHidden:${withHidden}`
      : `${
          defaultAddressStateSearchText
            ? `addressState:${defaultAddressStateSearchText},`
            : ''
        }withHidden:${withHidden}`
  );
  const [filteredCLOListing, setFilteredCLOListing] = useState<Array<any>>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [showNoResultWarning, setShowNoResultWarning] = useState<boolean>(
    false
  );
  const [orderBy, setOrderBy] = useState<string>(
    order === '1' ? 'distance' : order === '2' ? 'name' : '-name'
  );
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [skeletonColumnCount, setSkeletonColumnCount] = useState<number>(5);
  const [isPerformingUpdate, setIsPerformingUpdate] = useState<boolean>(false);
  const addressMenuOpenRef = useRef<boolean>(false);

  const sortListItems = [
    {
      label: 'Sort by distance (default)',
      value: '1'
    },
    {
      label: 'Sort by name (A-Z)',
      value: '2'
    },
    {
      label: 'Sort by name (Z-A)',
      value: '3'
    }
  ];

  const headers: Array<any> = [
    { key: 'id', header: '' },
    { key: 'name', header: '' },
    { key: 'locatorAmount', header: '' },
    { key: 'address', header: '' },
    { key: 'star', header: '' },
    { key: 'hidden', header: '' }
  ];

  const handlePageChange = ({
    page,
    pageSize
  }: {
    page: number;
    pageSize: number;
  }) => {
    setIsFirstLoad(false);
    setPageOffset((page - 1) * pageSize);
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const resetPagination = () => {
    setCurrentPage(1);
    setPageOffset(0);
  };

  const constructFilterString = (
    name: string,
    showHidden: boolean,
    addressState: string[]
  ) => {
    const filterValues = {
      name: name,
      withHidden: showHidden,
      addressState: addressState
    };
    const filterString = Object.entries({
      ...filterValues
    })
      .reduce(filterStringReducer, '')
      .substring(1);
    setFilterBy(filterString);
  };

  const filterStringReducer = (accumulator: any, currentValue: any) => {
    const [key, value] = currentValue;
    switch (key) {
      case 'name':
        accumulator = value ? `${accumulator},name:"%${value}%"` : accumulator;
        break;
      case 'withHidden':
        accumulator = `${accumulator},withHidden:${value}`;
        break;
      case 'addressState': {
        if (value.length > 0) {
          accumulator = `${accumulator},addressState:${createAddressStateSearchText(
            value
          )}`;
        }
        break;
      }
      default:
        break;
    }
    return accumulator;
  };

  const debouncedFunction = useCallback(
    Utils.debounce(async (args: any[]) => {
      handleChangeSearchBy(args[0], args[1], args[2]);
    }, 500),
    []
  );

  const handleChangeSearchBy = (
    searchText: string,
    showHidden: any,
    addressState: string[]
  ) => {
    setFilteredCLOListing([]);
    resetPagination();
    setIsFirstLoad(false);
    constructFilterString(searchText, showHidden, addressState);
  };

  const handleSearch = (e: any) => {
    setSearchText(e.target.value);
    if (e.target.value.length >= 2)
      debouncedFunction(e.target.value, withHidden, addressState);
    if (e.target.value.length === 0)
      debouncedFunction(e.target.value, withHidden, addressState);
  };

  const handleDropdownChange = (value: string) => {
    let order = 'distance';
    if (value === '2') {
      order = 'name';
    } else if (value === '3') {
      order = '-name';
    }

    setIsFirstLoad(false);
    setOrderBy(order);
    setOrder(value);
    if (order !== orderBy) {
      setFilteredCLOListing([]);
      resetPagination();
    }
  };

  const getOrderOptionItem = (value: string) => {
    if (value) {
      const matched = sortListItems.filter((o: any) => value.includes(o.value));
      return matched[0];
    }
    return [];
  };

  const handleAddressStateChange = ({
    selectedItems
  }: {
    selectedItems: any[];
  }) => {
    const values = selectedItems.map((i) => i.value);
    setAddressState(values);
    if (!addressMenuOpenRef.current && values.length === 0) {
      constructFilterString(searchText, withHidden, values);
    }
  };

  const handleAddressStateMenuChange = (open: boolean) => {
    addressMenuOpenRef.current = open;
    if (!open) {
      setIsFirstLoad(false);
      constructFilterString(searchText, withHidden, addressState);
    }
  };

  const handleToggleChange = (value: boolean) => {
    setFilteredCLOListing([]);
    resetPagination();
    setIsFirstLoad(false);
    setWithHidden(value);
    constructFilterString(searchText, value, addressState);
  };

  const handleCLORowClick = (cloId: number) => {
    handleCLOViewDetailsClick(cloId, '');
  };

  useEffect(() => {
    if (!loadingCLOListing && Object.keys(cloList).length !== 0) {
      const cloListInfo = cloList.Info;
      const cloListOrgs = cloList.LocatingOrganisations;

      const filteredList = cloListOrgs.map((org: any) => {
        const data = {
          id: org.id,
          name: org.name,
          locatorAmount: org.details.locatorAmount,
          address: org.Address,
          star: !!org.isFav,
          hidden: !!org.isHidden
        };
        return data;
      });
      setShowNoResultWarning(true);
      setFilteredCLOListing(filteredList);
      setTotalItems(cloListInfo.count);
    }
  }, [cloList, loadingCLOListing]);

  useEffect(() => {
    if (
      enquiryDetail &&
      Object.keys(enquiryDetail).length !== 0 &&
      !isPerformingUpdate &&
      isAuthenticated
    ) {
      const params = {
        filter: filterBy,
        limit: pageSize,
        offset: pageOffset,
        include: 'Address',
        eId: enquiryDetail?.id,
        fields: 'id,name,details',
        orderBy: orderBy
      };
      dispatch(fetchCLOListing(params));
    }
  }, [
    enquiryDetail,
    dispatch,
    pageOffset,
    pageSize,
    currentPage,
    filterBy,
    orderBy,
    withHidden,
    isPerformingUpdate,
    isAuthenticated
  ]);

  useEffect(() => {
    if (smallScreen.includes(screenSize)) {
      setSkeletonColumnCount(2);
    }
    if (middleScreen.includes(screenSize)) {
      setSkeletonColumnCount(3);
    }
    if (bigScreen.includes(screenSize)) {
      setSkeletonColumnCount(5);
    }
  }, [screenSize, bigScreen, middleScreen, smallScreen]);

  useEffect(() => {
    if (
      addingCLOAsFavourite ||
      removingCLOAsFavourite ||
      unhidingCLO ||
      hidingCLO
    ) {
      setIsPerformingUpdate(true);
    } else {
      setIsPerformingUpdate(false);
    }
  }, [addingCLOAsFavourite, removingCLOAsFavourite, unhidingCLO, hidingCLO]);

  console.log('enquiryDetail in CLOModalListing', enquiryDetail);

  return (
    <div className="clo-listing">
      <div className="listing-container">
        <div className="header">
          {enquiryDetail?.Address &&
            Object.keys(enquiryDetail?.Address).length !== 0 && (
              <div className="address">
                {Utils.getFullAddress(enquiryDetail.Address)}
              </div>
            )}
        </div>

        <div className="listing-table-container">
          {((!loadingCLOListing && !isPerformingUpdate) ||
            (loadingCLOListing && !isFirstLoad)) && (
            <DataTable headers={headers} rows={filteredCLOListing || []}>
              {({ getTableProps, getRowProps, rows }: any) => {
                return (
                  <TableContainer>
                    <div className="custom-table-toolbar">
                      <TableToolbar>
                        <TableToolbarContent>
                          <TableToolbarSearch
                            onChange={handleSearch}
                            expanded
                            placeHolderText="Filter"
                            value={searchText}
                          />
                        </TableToolbarContent>
                      </TableToolbar>
                      <MultiSelect
                        id="address-state"
                        titleText=""
                        label="State"
                        items={addressStateListItems}
                        initialSelectedItems={getAddressStateOptionItem(
                          addressState
                        )}
                        itemToString={(item: any) => item.label}
                        onChange={handleAddressStateChange}
                        onMenuChange={handleAddressStateMenuChange}
                      />
                      <Dropdown
                        id="sort"
                        titleText=""
                        label="Sort"
                        items={sortListItems}
                        itemToString={(item: any) => item.label}
                        initialSelectedItem={getOrderOptionItem(order)}
                        onChange={(item: any) =>
                          handleDropdownChange(item?.selectedItem?.value)
                        }
                      />
                    </div>
                    {!loadingCLOListing && filteredCLOListing.length >= 1 && (
                      <Table {...getTableProps()}>
                        <TableBody>
                          {rows.map((row: any, index: any) => (
                            <CLOModalListingRow
                              row={row}
                              getRowProps={getRowProps}
                              key={index}
                              handleCLOViewDetailsClick={handleCLORowClick}
                              screenSize={screenSize}
                            />
                          ))}
                        </TableBody>
                      </Table>
                    )}
                    {!loadingCLOListing &&
                      filteredCLOListing.length === 0 &&
                      showNoResultWarning && (
                        <div className="no-result">No results found</div>
                      )}
                    {loadingCLOListing && (
                      <div className="clo-loading-container">
                        <DataTableSkeleton
                          showHeader={false}
                          showToolbar={false}
                          compact={true}
                          rowCount={5}
                          columnCount={skeletonColumnCount}
                        />
                      </div>
                    )}
                  </TableContainer>
                );
              }}
            </DataTable>
          )}
          {(isPerformingUpdate || loadingCLOListing) && isFirstLoad && (
            <DataTableSkeleton
              showHeader={false}
              showToolbar={false}
              compact={true}
              rowCount={5}
              columnCount={skeletonColumnCount}
            />
          )}
          {filteredCLOListing.length >= 1 && (
            <>
              <CustomPagination
                onChange={(data: any) => handlePageChange(data)}
                totalItems={totalItems}
                page={currentPage}
                pageSize={pageSize}
              />
            </>
          )}
        </div>
      </div>
      <div className="show-all-toggle-container">
        <ToggleSmall
          defaultToggled={withHidden}
          aria-label="Show hidden"
          labelA="Show hidden"
          labelB="Show hidden"
          id="toggle-show-hidden"
          onToggle={(value: boolean) => handleToggleChange(value)}
        />
      </div>
    </div>
  );
};

export default CLOModalListing;
